import React, { useState, useRef, useEffect } from 'react';

interface SearchComponentProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (value: string) => void;
  placeholder?: string;
  suggestions?: string[];
  className?: string;
}

const SearchComponent: React.FC<SearchComponentProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search...",
  suggestions = [],
  className = ""
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value && suggestions.length > 0) {
      const filtered = suggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
    setActiveSuggestion(-1);
  }, [value, suggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Show suggestions only if there's input and there are matching suggestions
    if (newValue.trim() && filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    onSelect?.(suggestion);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestion >= 0) {
        handleSuggestionClick(filteredSuggestions[activeSuggestion]);
      } else if (onSelect) {
        onSelect(value);
        setShowSuggestions(false);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  };

  return (
    <div className={`relative w-full max-w-full ${className}`}>
      <div className="relative group w-full">
        {/* Elegant outer glow */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/20 via-transparent to-primary/20 blur-md opacity-50 group-hover:opacity-75 group-focus-within:opacity-100 transition-all duration-700"></div>
        
        {/* Subtle inner glow */}
        <div className="absolute inset-[1px] rounded-xl bg-gradient-to-r from-transparent via-primary/5 to-transparent group-focus-within:from-primary/10 group-focus-within:to-primary/10 transition-all duration-500"></div>
        
        {/* Main input container */}
        <div className="relative">
          <input 
            ref={inputRef}
            placeholder={placeholder} 
            type="text" 
            name="text" 
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (value.trim() && filteredSuggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => {
              setTimeout(() => setShowSuggestions(false), 200);
            }}
            className="widget-nodrag bg-card/80 backdrop-blur-xl border border-border/50 w-full h-[48px] rounded-xl text-foreground px-12 text-sm focus:outline-none placeholder:text-muted-foreground transition-all duration-300 focus:border-primary/40 focus:bg-card/90 focus:shadow-lg focus:shadow-primary/10" 
          />
          
          {/* Search icon */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground group-focus-within:text-primary transition-colors duration-300">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
          </div>
        </div>

        {/* Elegant suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-[52px] left-0 right-0 max-h-[200px] overflow-y-auto bg-card/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl z-50 animate-fade-in min-w-0">
            {filteredSuggestions.slice(0, 6).map((suggestion, index) => (
              <div
                key={index}
                className={`px-4 py-3 cursor-pointer border-b border-border/30 last:border-b-0 transition-all duration-200 text-sm ${
                  index === activeSuggestion 
                    ? 'bg-primary/10 text-foreground' 
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchComponent;